// src/pages/financeiro/RecebimentoCad.js
import React, { useEffect, useState } from 'react';
import Input from '../../components/input/Input.js';
import Select from '../../components/input/Select.js';
import ModalContainer from '../../components/modalForm/ModalContainer.js';
import ModalHeader from '../../components/modalForm/ModalHeader.js';
import ModalBody from '../../components/modalForm/ModalBody.js';
import ModalFooter from '../../components/modalForm/ModalFooter.js';
import { useCliente } from '../../contexts/pessoas/ClienteContext.js';
import { useFormaPag } from '../../contexts/financeiro/FormaPagContext.js';
import { useFilial } from '../../contexts/administrativo/FilialContext.js';
import { useRecebimento } from '../../contexts/financeiro/RecebimentoContext.js';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { criarParcelasRecebimento, obterPrimeiroVencimentoRecebimento, validarRecebimentoManual } from '../../models/financeiro/Recebimento.js';
import { formatCurrency, formatNumeric, normalizeDecimalInputBr, parseNumeric } from '../../utils/numberUtils.js';
import { ModalActionButton } from '../../components/modalForm/ModalActions';

const dataHoje = () => obterPrimeiroVencimentoRecebimento(null);

const RecebimentoCad = ({ show, onHide, onSave }) => {
    const { clientes, showClienteSearch, showClienteCad } = useCliente();
    const { formasPag } = useFormaPag();
    const { filialLogin } = useFilial();
    const { addRecebimento } = useRecebimento();
    const { showMessage } = useMessage();
    const [salvando, setSalvando] = useState(false);
    const [clienteNome, setClienteNome] = useState('');
    const [valorTotal, setValorTotal] = useState('');
    const [quantidadeParcelas, setQuantidadeParcelas] = useState(1);
    const [primeiroVencimento, setPrimeiroVencimento] = useState(dataHoje());
    const [recebimento, setRecebimento] = useState({
        clienteId: '', formaPagId: '', dataCriacao: dataHoje(), parcelas: [],
    });

    useEffect(() => {
        if (!show) return;
        setClienteNome('');
        setValorTotal('');
        setQuantidadeParcelas(1);
        setPrimeiroVencimento(dataHoje());
        setRecebimento({ clienteId: '', formaPagId: '', dataCriacao: dataHoje(), parcelas: [] });
    }, [show]);

    const selecionarCliente = (cliente) => {
        setRecebimento((atual) => ({ ...atual, clienteId: cliente?.id || '' }));
        setClienteNome(cliente?.pessoa?.nome || cliente?.nome || '');
    };

    const validarClienteDigitado = (id) => {
        if (!id) return selecionarCliente(null);
        const cliente = clientes.find((item) => Number(item.id) === Number(id));
        if (cliente) return selecionarCliente(cliente);
        selecionarCliente(null);
        showMessage('Cliente não encontrado!', 'warning');
    };

    const cadastrarCliente = async () => selecionarCliente(await showClienteCad(null, false));

    const alterarFormaPagamento = (formaPagId) => {
        const id = Number(formaPagId) || '';
        const formaPag = formasPag.find((item) => Number(item.id) === Number(id));
        setRecebimento((atual) => ({ ...atual, formaPagId: id }));
        setPrimeiroVencimento(obterPrimeiroVencimentoRecebimento(formaPag));
    };

    const gerarParcelas = () => {
        const valorTotalNumerico = parseNumeric(valorTotal);
        if (valorTotalNumerico <= 0 || Number(quantidadeParcelas) <= 0 || !recebimento.formaPagId || !primeiroVencimento) {
            showMessage('Informe valor total, quantidade, forma de pagamento e primeiro vencimento.', 'warning');
            return;
        }
        setRecebimento((atual) => ({
            ...atual,
            parcelas: criarParcelasRecebimento(valorTotalNumerico, quantidadeParcelas, primeiroVencimento),
        }));
    };

    const alterarParcela = (indice, campo, valor) => setRecebimento((atual) => ({
        ...atual,
        parcelas: atual.parcelas.map((parcela, posicao) => posicao === indice
            ? { ...parcela, [campo]: valor }
            : parcela),
    }));

    const salvar = async () => {
        const payload = { ...recebimento, filialId: filialLogin?.id };
        const erro = validarRecebimentoManual(payload);
        if (!filialLogin?.id) return showMessage('Filial do login não identificada.', 'warning');
        if (erro) return showMessage(erro, 'warning');

        setSalvando(true);
        try {
            await addRecebimento(payload);
            await onSave?.();
            onHide();
        } finally {
            setSalvando(false);
        }
    };

    return (
        <ModalContainer show={show} largura="850px" zIndex={1070} variant="cadastro" onHide={onHide}>
            <ModalHeader title="Incluir recebimento manual" onHide={onHide} />
            <ModalBody>
                <div className="d-flex gap-2 flex-wrap align-items-end mb-3">
                    <Input label="Cliente" value={recebimento.clienteId} size={100}
                        onChange={(e) => setRecebimento({ ...recebimento, clienteId: Number(e.target.value) || '' })}
                        onBlur={(e) => validarClienteDigitado(e.target.value)}
                        btnBuscar={async () => selecionarCliente(await showClienteSearch())}
                        btnAdd={cadastrarCliente} />
                    <Input label="Nome do cliente" value={clienteNome} size={260} disabled />
                    <Input label="Emissão" type="date" value={recebimento.dataCriacao} size={140}
                        onChange={(e) => setRecebimento({ ...recebimento, dataCriacao: e.target.value })} />
                    <Select label="Forma de pagamento" value={recebimento.formaPagId} size={210}
                        onChange={(e) => alterarFormaPagamento(e.target.value)}
                        options={[{ value: '', label: 'Selecione' }, ...formasPag.filter((item) => !item.inativo).map((item) => ({ value: item.id, label: item.descricao }))]} />
                </div>
                <div className="d-flex gap-2 align-items-end mb-3">
                    <Input label="Valor total" type="text" value={valorTotal} size={150}
                        style={{ textAlign: 'right' }}
                        onChange={(e) => setValorTotal(normalizeDecimalInputBr(e.target.value))}
                        onBlur={() => setValorTotal(formatNumeric(valorTotal))} />
                    <Input label="Parcelas" type="number" value={quantidadeParcelas} size={100}
                        onChange={(e) => setQuantidadeParcelas(Math.max(1, Number(e.target.value) || 1))} />
                    <Input label="Primeiro vencimento" type="date" value={primeiroVencimento} size={160}
                        onChange={(e) => setPrimeiroVencimento(e.target.value)} />
                    <button type="button" className="btn btn-secondary" onClick={gerarParcelas}>Gerar parcelas</button>
                </div>
                <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle">
                        <thead><tr><th>Parcela</th><th>Vencimento</th><th>Valor</th></tr></thead>
                        <tbody>
                            {recebimento.parcelas.map((parcela, indice) => (
                                <tr key={indice}>
                                    <td>{indice + 1}</td>
                                    <td><input className="form-control" type="date" value={parcela.dataVencimento}
                                        onChange={(e) => alterarParcela(indice, 'dataVencimento', e.target.value)} /></td>
                                    <td><input className="form-control" type="number" step="0.01" value={parcela.valorParcela}
                                        onChange={(e) => alterarParcela(indice, 'valorParcela', Number(e.target.value))} /></td>
                                </tr>
                            ))}
                            {!recebimento.parcelas.length && <tr><td colSpan="3" className="text-center">Gere as parcelas para continuar.</td></tr>}
                        </tbody>
                        {!!recebimento.parcelas.length && <tfoot><tr><th colSpan="2">Total</th><th>{formatCurrency(recebimento.parcelas.reduce((soma, p) => soma + Number(p.valorParcela || 0), 0))}</th></tr></tfoot>}
                    </table>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn btn-secondary" onClick={onHide} disabled={salvando}>Cancelar</button>
                <ModalActionButton type="button" className="btn btn-success" onClick={salvar} disabled={salvando} action="save">{salvando ? 'Salvando...' : 'Salvar'}</ModalActionButton>
            </ModalFooter>
        </ModalContainer>
    );
};

export default RecebimentoCad;
